import { useRef, useState, useCallback, useEffect } from 'react';
import RHTV_ChannelItem from './Channel/RHTV-ChannelItem';
import { getBooleanParam } from '@/utils/urlParams';

import styles from './RHTV-Channels.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

import { useDebug } from "@/utils/useDebugLog";
const { log } = useDebug({ name: 'RHTV_Channels', bgColor: 'fuchsia', enabled: false });

// ----------------------------------------

interface RockHallTvChannelsProps {
  channels: RockHallTvChannel[];
  rootRef?: React.RefObject<HTMLDivElement>; // optional, provided by parent
  rowRef?: React.RefObject<HTMLDivElement>;  // optional, provided by parent
  onChannelClick?: (channel: RockHallTvChannel, index: number) => void;
  onChannelFocus?: (index: number, element: HTMLElement) => void;
  onTriggerKick?: () => void; // callback to trigger kick animation
  isKickComplete?: boolean; // whether kick animation has completed (state 2)
  showModal?: boolean; // whether modal is open (state 3)
  className?: string; // optional className from parent
  isPortraitMode?: boolean;
  maxItemSize?: { width: number; height: number } | null; // Cap channel item size
  isTouchDevice?: boolean; // whether device supports touch
  sectionLabel: string;
  channelLabel: string;
}

export default function RHTV_Channels({ channels, rootRef, rowRef, onChannelClick, onChannelFocus, onTriggerKick, isKickComplete = false, showModal = false, className: parentClassName, isPortraitMode, maxItemSize, isTouchDevice = false, sectionLabel, channelLabel }: RockHallTvChannelsProps) {

  const isDebugMode = getBooleanParam('debug');

  const localRootRef = useRef<HTMLDivElement | null>(null);
  const localRowRef = useRef<HTMLDivElement | null>(null);

  // Prefer refs passed from parent (so parent can measure / animate)
  const appliedRootRef = rootRef ?? localRootRef;
  const appliedRowRef = rowRef ?? localRowRef;

  // -------------------- Shared Title Font-Size Management
  // Track the minimum font-size needed across all channel items
  // so all titles use the same size for visual consistency

  const [minTitleFontSize, setMinTitleFontSize] = useState<number | null>(null);

  // Reset minimum font-size when maxItemSize changes (indicates resize/orientation change)
  // This allows recalculation with new dimensions
  useEffect(() => {
    log('maxItemSize changed, resetting minTitleFontSize', maxItemSize);
    setMinTitleFontSize(null);
  }, [maxItemSize]);

  const handleTitleFontSizeCalculated = useCallback((fontSize: number, channelIndex: number) => {
    setMinTitleFontSize((prevMin) => {
      // If no min set yet, or this is smaller, update it
      if (prevMin === null || fontSize < prevMin) {
        // Only update if significantly different (avoid floating point precision issues)
        if (prevMin === null || Math.abs(fontSize - prevMin) > 0.1) {
          // log(`New min font-size: ${fontSize}px from channel ${channelIndex}`);
          return fontSize;
        }
      }
      return prevMin;
    });
  }, []);

  // -------------------- Render

  log('Render', { channelCount: channels?.length });

  return (
    <section
      id="main-content"
      className={cx(["rock-hall-tv-channels", { debug: isDebugMode }, { portrait: isPortraitMode }, parentClassName])}
      ref={appliedRootRef}
      tabIndex={-1}
      aria-label={sectionLabel}
    >
      <div className={cx("row")} ref={appliedRowRef}>
        <div className={cx("channels-row-sidespace")} role="presentation" />
        <div className={cx("channels-wrapper")} role="list">
          {channels?.map((channel, index) => (
            <div key={channel?.uri} role="listitem">
              <RHTV_ChannelItem
                {...channel}
                index={index}
                isPortraitMode={isPortraitMode}
                isKickComplete={isKickComplete}
                showModal={showModal}
                channelLabel={channelLabel}
                onChannelClick={onChannelClick}
                onChannelFocus={onChannelFocus}
                onTriggerKick={onTriggerKick}
                maxItemSize={maxItemSize}
                isTouchDevice={isTouchDevice}
                sharedMinFontSize={minTitleFontSize}
                onFontSizeCalculated={handleTitleFontSizeCalculated}
                shouldLoadVideo={true}
              />
            </div>
          ))}
        </div>
        <div className={cx("channels-row-sidespace")} role="presentation" />
        <div className={cx("channels-row-bottomspace")} role="presentation" /> {/* Bottom spacer */}
      </div>
    </section>
  );

}
